import React, { useCallback, useEffect, useMemo, useState } from "react";

const FILTERS = [
  ["pending", "Onay Bekleyenler"],
  ["approved", "Onaylananlar"],
  ["rejected", "Reddedilenler"],
  ["all", "Tümü"],
];

const COLORS = {
  navy: "#102a43",
  blue: "#1463b8",
  orange: "#f28c28",
  green: "#168a4a",
  red: "#b91c1c",
  muted: "#627d98",
  border: "#d9e2ec",
  surface: "#f4f7fc",
};

async function readPayload(response) {
  const raw = await response.text();
  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch {
    return { message: raw };
  }
}

function localized(value) {
  if (!value) return "";
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed?.tr || parsed?.en || parsed?.de || value;
    } catch {
      return value;
    }
  }

  return value.tr || value.en || value.de || "";
}

function imageList(item) {
  let images = item?.images;

  if (typeof images === "string") {
    try {
      images = JSON.parse(images);
    } catch {
      images = [];
    }
  }

  return [item?.cover_image, ...(Array.isArray(images) ? images : [])]
    .filter(Boolean)
    .filter((value, index, list) => list.indexOf(value) === index);
}

function money(value, currency) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: currency || "TRY",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function dateValue(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("tr-TR");
}

function statusInfo(status) {
  if (status === "approved") {
    return { label: "Onaylandı", color: COLORS.green, background: "#ecfdf5" };
  }

  if (status === "rejected") {
    return { label: "Reddedildi", color: COLORS.red, background: "#fff1f2" };
  }

  return { label: "Onay Bekliyor", color: "#9a3412", background: "#fff7ed" };
}

export default function MarketplaceIlanOnaylari({ API, token, setMessage }) {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filter !== "all") params.set("approval_status", filter);

      const response = await fetch(
        `${API}/marketplace/admin/product-approvals?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const payload = await readPayload(response);
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.message || "Pazaryeri ilanları alınamadı.");
      }

      setItems(Array.isArray(payload?.data?.items) ? payload.data.items : []);
      setCounts(payload?.data?.counts || { pending: 0, approved: 0, rejected: 0, all: 0 });
    } catch (error) {
      setItems([]);
      setMessage?.({
        type: "error",
        text: error.message || "Pazaryeri ilanları alınamadı.",
      });
    } finally {
      setLoading(false);
    }
  }, [API, filter, setMessage, token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr-TR");
    if (!term) return items;

    return items.filter((item) =>
      [
        localized(item.name),
        localized(item.description),
        item.kurum_name,
        item.kurum_code,
        item.kurum_id,
        item.category_name ? localized(item.category_name) : "",
        item.brand,
        item.model,
        item.sku,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(term)
    );
  }, [items, search]);

  async function review(item, action) {
    const reason = String(notes[item.id] || "").trim();

    if (action === "reject" && reason.length < 5) {
      setMessage?.({
        type: "error",
        text: "Reddetmek için en az 5 karakterlik gerekçe yazmalısınız.",
      });
      return;
    }

    const verb = action === "approve" ? "onaylamak" : "reddetmek";
    if (!window.confirm(`“${localized(item.name)}” ilanını ${verb} istiyor musunuz?`)) {
      return;
    }

    setActionId(item.id);

    try {
      const response = await fetch(
        `${API}/marketplace/admin/product-approvals/${item.id}/review`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ action, rejection_reason: reason || null }),
        }
      );

      const payload = await readPayload(response);
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.message || "İlan onay işlemi tamamlanamadı.");
      }

      setNotes((previous) => ({ ...previous, [item.id]: "" }));
      setMessage?.({
        type: "success",
        text: action === "approve" ? "İlan onaylandı ve yayına hazır." : "İlan reddedildi.",
      });
      await fetchItems();
    } catch (error) {
      setMessage?.({ type: "error", text: error.message || "İşlem tamamlanamadı." });
    } finally {
      setActionId(null);
    }
  }

  async function removeItem(item) {
    if (!window.confirm(`“${localized(item.name)}” ilanı silinsin mi? Kayıt denetim amacıyla korunacaktır.`)) {
      return;
    }

    setActionId(item.id);

    try {
      const response = await fetch(
        `${API}/marketplace/admin/product-approvals/${item.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const payload = await readPayload(response);
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.message || "İlan silinemedi.");
      }

      setMessage?.({ type: "success", text: "İlan yayından kaldırıldı." });
      await fetchItems();
    } catch (error) {
      setMessage?.({ type: "error", text: error.message || "İlan silinemedi." });
    } finally {
      setActionId(null);
    }
  }

  return (
    <section style={{ marginTop: 26 }}>
      <div
        style={{
          padding: 22,
          borderRadius: 20,
          border: `1px solid ${COLORS.border}`,
          background: "#fff",
          boxShadow: "0 14px 35px rgba(15,42,67,.07)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ color: COLORS.orange, fontSize: 11, fontWeight: 900, letterSpacing: ".09em" }}>
              MESYAT PAZARYERİ DENETİM MERKEZİ
            </div>
            <h2 style={{ margin: "6px 0 5px", color: COLORS.navy, fontSize: 25 }}>
              Pazaryeri İlan Onayları ({counts.all || items.length})
            </h2>
            <p style={{ margin: 0, color: COLORS.muted, lineHeight: 1.5 }}>
              Kurumların eklediği veya güncellediği ürün ve ilanları yayınlanmadan önce inceleyin.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchItems}
            disabled={loading}
            style={{
              alignSelf: "flex-start",
              padding: "10px 14px",
              borderRadius: 12,
              border: 0,
              color: "#fff",
              background: COLORS.blue,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {loading ? "Yükleniyor..." : "Yenile"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
          {FILTERS.map(([value, label]) => (
            <button
              type="button"
              key={value}
              onClick={() => setFilter(value)}
              style={{
                padding: "9px 12px",
                borderRadius: 999,
                border: filter === value ? `1px solid ${COLORS.blue}` : `1px solid ${COLORS.border}`,
                color: filter === value ? "#fff" : COLORS.navy,
                background: filter === value ? COLORS.blue : "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {label} ({value === "all" ? counts.all : counts[value] || 0})
            </button>
          ))}

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Kurum, ilan, kategori veya ürün ara..."
            style={{
              flex: "1 1 280px",
              minHeight: 40,
              padding: "0 12px",
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
            }}
          />
        </div>

        <div style={{ display: "grid", gap: 13, marginTop: 18 }}>
          {!loading && visibleItems.length === 0 && (
            <div style={{ padding: 30, textAlign: "center", borderRadius: 16, background: COLORS.surface, color: COLORS.muted, fontWeight: 800 }}>
              Bu filtrede ilan bulunmuyor.
            </div>
          )}

          {visibleItems.map((item) => {
            const info = statusInfo(item.approval_status);
            const images = imageList(item);
            const expanded = expandedId === item.id;
            const busy = actionId === item.id;

            return (
              <article key={item.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 17, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "110px minmax(0,1fr)", gap: 14, padding: 15 }}>
                  <div style={{ height: 105, borderRadius: 13, overflow: "hidden", background: COLORS.surface }}>
                    {images[0] ? (
                      <img src={images[0]} alt={localized(item.name)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ height: "100%", display: "grid", placeItems: "center", color: COLORS.muted, fontWeight: 900 }}>Görsel Yok</div>
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div>
                        <h3 style={{ margin: 0, color: COLORS.navy }}>{localized(item.name) || `İlan #${item.id}`}</h3>
                        <div style={{ marginTop: 5, color: COLORS.muted, fontSize: 13, fontWeight: 800 }}>
                          {item.kurum_name || "Kurum"} · Kurum ID: {item.kurum_id} · Kod: {item.kurum_code || "—"}
                        </div>
                      </div>

                      <span style={{ alignSelf: "flex-start", padding: "7px 10px", borderRadius: 999, color: info.color, background: info.background, fontWeight: 900, fontSize: 12 }}>
                        {info.label}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap", color: COLORS.navy, fontSize: 13 }}>
                      <b>{money(item.price, item.currency)}</b>
                      <span>Kategori: {localized(item.category_name) || "—"}</span>
                      <span>Eklenme: {dateValue(item.created_at)}</span>
                      <span>İlan durumu: {item.status || "—"}</span>
                    </div>

                    <p style={{ margin: "9px 0 0", color: COLORS.muted, lineHeight: 1.45 }}>
                      {localized(item.description) || "Açıklama eklenmemiş."}
                    </p>
                  </div>
                </div>

                {expanded && (
                  <div style={{ padding: "0 15px 15px" }}>
                    {images.length > 0 && (
                      <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8 }}>
                        {images.map((src) => (
                          <img key={src} src={src} alt="İlan" style={{ width: 115, height: 85, borderRadius: 10, objectFit: "cover", border: `1px solid ${COLORS.border}` }} />
                        ))}
                      </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, padding: 12, borderRadius: 12, background: COLORS.surface, color: COLORS.navy, fontSize: 13 }}>
                      <span><b>Marka:</b> {item.brand || "—"}</span>
                      <span><b>Model:</b> {item.model || "—"}</span>
                      <span><b>SKU:</b> {item.sku || "—"}</span>
                      <span><b>Tip:</b> {item.product_type || "—"}</span>
                      <span><b>Satış tipi:</b> {item.sale_type || "—"}</span>
                      <span><b>Güncelleme:</b> {dateValue(item.updated_at)}</span>
                    </div>
                  </div>
                )}

                <div style={{ padding: 14, borderTop: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
                  <textarea
                    value={notes[item.id] || ""}
                    onChange={(event) => setNotes((previous) => ({ ...previous, [item.id]: event.target.value }))}
                    placeholder="Red gerekçesi (reddetme işleminde zorunlu)..."
                    style={{ width: "100%", minHeight: 62, boxSizing: "border-box", resize: "vertical", padding: 10, borderRadius: 11, border: `1px solid ${COLORS.border}`, fontFamily: "inherit" }}
                  />

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                    <button type="button" onClick={() => setExpandedId(expanded ? null : item.id)} style={{ padding: "10px 13px", borderRadius: 11, border: `1px solid ${COLORS.border}`, background: "#fff", color: COLORS.navy, fontWeight: 900, cursor: "pointer" }}>
                      {expanded ? "Detayı Kapat" : "Detay"}
                    </button>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" disabled={busy} onClick={() => removeItem(item)} style={{ padding: "10px 13px", borderRadius: 11, border: `1px solid #fecaca`, background: "#fff1f2", color: COLORS.red, fontWeight: 900, cursor: "pointer" }}>
                        Sil
                      </button>
                      <button type="button" disabled={busy} onClick={() => review(item, "reject")} style={{ padding: "10px 13px", borderRadius: 11, border: `1px solid #fecaca`, background: "#fff", color: COLORS.red, fontWeight: 900, cursor: "pointer" }}>
                        Reddet
                      </button>
                      <button type="button" disabled={busy} onClick={() => review(item, "approve")} style={{ padding: "10px 14px", borderRadius: 11, border: 0, background: COLORS.green, color: "#fff", fontWeight: 900, cursor: "pointer" }}>
                        {busy ? "İşleniyor..." : "Onayla"}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
